import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import { BRAND, GREY, BASE, BORDER } from "../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../utils/constants/UI";
import Typography from "../../../../components/Typography/Typography";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import DashboardLoader from "../../../../components/Loaders/DashboardLoader";

// ─── Data hook ────────────────────────────────────────────────────────────────
import useBillingData from "../../../../hooks/useBillingData";

// ─── Section components ───────────────────────────────────────────────────────
import BillingOverviewSection from "./Billing/BillingOverviewSection";
import BillingPlansSection from "./Billing/BillingPlansSection";
import BillingPaymentSection from "./Billing/BillingPaymentSection";
import BillingSettingsSection from "./Billing/BillingSettingsSection";
import BillingCancelModal from "./Billing/BillingCancelModal";
import BillingChangePlanModal from "./Billing/BillingChangePlanModal";
import BillingSeatsSection from "./Billing/BillingSeatsSection";

// ─── Tab definitions ──────────────────────────────────────────────────────────
const TABS = [
  { id: "overview", label: "Overview", icon: ICON_LABELS.DASHBOARD },
  { id: "plans", label: "Plans", icon: ICON_LABELS.SHOPPING_CART },
  // { id: "seats", label: "Team Seats", icon: ICON_LABELS.GROUPS },
  { id: "payment", label: "Payment Methods", icon: ICON_LABELS.CREDIT_CARD },
  { id: "settings", label: "Settings", icon: ICON_LABELS.SETTINGS },
];

// ─── Shell styles (header + tab bar only - section styles live in each component)
const shellStyles = {
  container: {
    margin: "16px 0px 0px 16px",
    borderRadius: "12px",
    height: "100%",
  },
  mainWrapper: {
    background: BASE.WHITE,
    height: "100%",
    overflow: "auto",
    borderRadius: "12px",
  },
  header: {
    padding: "22px 24px 20px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    borderBottom: `1px solid ${BORDER.DEFAULT}`,
    width: "100%",
  },
  tabBar: {
    display: "flex",
    gap: "6px",
    borderBottom: `1px solid ${BORDER.DEFAULT}`,
    padding: "0 24px",
  },
  tabItem: {
    minHeight: "42px",
    padding: "11px 14px 10px",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    borderBottom: "2px solid transparent",
    transition:
      "border-color 0.18s ease, background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease",
    borderRadius: "6px 6px 0 0",
  },
  tabItemActive: {
    minHeight: "42px",
    padding: "11px 14px 10px",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    borderBottom: `2px solid ${BRAND.PRIMARY}`,
    borderRadius: "6px 6px 0 0",
    background: BASE.WHITE,
    boxShadow: `0 10px 20px -24px ${GREY.SECONDARY}`,
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
//  Billing - container / orchestrator
//
//  Owns only UI state (active tab, billing cycle, plan view, selected plan).
//  All data fetching and mutation logic lives in useBillingData.
// ═══════════════════════════════════════════════════════════════════════════════
const Billing = () => {
  // ─── UI-only state ─────────────────────────────────────────────────
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const subtab = searchParams.get("subtab");
    return TABS.some((t) => t.id === subtab) ? subtab : "overview";
  });
  const [billingCycle, setBillingCycle] = useState("month");
  const [planView, setPlanView] = useState("tools");
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  // Sync sub-tab when the ?subtab= query param changes (e.g. external nav)
  useEffect(() => {
    const subtab = searchParams.get("subtab");
    if (subtab && TABS.some((t) => t.id === subtab) && subtab !== activeTab) {
      setActiveTab(subtab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ─── Data + API ────────────────────────────────────────────────────
  const billing = useBillingData();

  return (
    <Box style={shellStyles.container}>
      <Box style={shellStyles.mainWrapper}>
        {/* Header - matches BuyCredits / BuyPackage exactly */}
        <Box style={shellStyles.header}>
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              size="md"
              color={GREY.PRIMARY}
              fontWeight={fontWeight.SEMIBOLD}
              label="Billing & Subscription"
            />
            {/* Header credit summary - preserved, re-enable when needed
            <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box style={{ textAlign: "right" }}>
                <TypographyInline
                  size="xs"
                  fontWeight={fontWeight.BOLD}
                  color={SUCCESS.W_600}
                  label="20,000 CREDITS TOTAL"
                />
                <TypographyInline
                  size="micro"
                  color={GREY.TERTIARY}
                  label="EST. MONTHLY COST: $129.00"
                />
              </Box>
              <Box
                style={{
                  width: "36px",
                  height: "36px",
                  background: PRIMARY.W_50,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconComponent
                  iconLabel={ICON_LABELS.ATTACH_MONEY}
                  style={{ color: BRAND.PRIMARY, fontSize: "20px" }}
                />
              </Box>
            </Box>
            */}
          </Box>
          <TypographyInline
            size="sm"
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            label="Manage your plan, credits, payment methods and billing preferences"
          />
        </Box>

        {/* Tab Bar */}
        <Box style={shellStyles.tabBar}>
          {TABS.map((tab) => (
            <Box
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setActiveTab(tab.id);
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={activeTab === tab.id}
              style={
                activeTab === tab.id
                  ? shellStyles.tabItemActive
                  : shellStyles.tabItem
              }
              sx={{
                "&:hover": {
                  background:
                    activeTab === tab.id ? BASE.WHITE : BORDER.DISABLED,
                  borderBottomColor:
                    activeTab === tab.id ? BRAND.PRIMARY : BORDER.HOVER,
                },
                "&:focus-visible": {
                  outline: `2px solid ${BRAND.PRIMARY}`,
                  outlineOffset: "-2px",
                },
              }}
            >
              <TypographyInline
                size="sm"
                fontWeight={
                  activeTab === tab.id
                    ? fontWeight.SEMIBOLD
                    : fontWeight.REGULAR
                }
                color={activeTab === tab.id ? BRAND.PRIMARY : GREY.SECONDARY}
                label={tab.label}
              />
            </Box>
          ))}
        </Box>

        {/* Tab Content */}
        {billing.loading ? (
          <DashboardLoader message="Loading your billing details..." />
        ) : (
          <>
            {activeTab === "overview" && (
              <BillingOverviewSection
                billingPlan={billing.billingPlan}
                creditUsage={billing.creditUsage}
                invoices={billing.invoices}
                setActiveTab={setActiveTab}
                isSdr={billing.isSdr}
                topUps={billing.topUps}
                buyingPlanId={billing.buyingPlanId}
                handleBuyPackage={billing.handleBuyPackage}
              />
            )}

            {activeTab === "plans" && (
              <BillingPlansSection
                packages={billing.packages}
                loading={billing.loading}
                billingCycle={billingCycle}
                setBillingCycle={setBillingCycle}
                planView={planView}
                setPlanView={setPlanView}
                currentSubscription={billing.currentSubscription}
                selectedPlanId={selectedPlanId}
                setSelectedPlanId={setSelectedPlanId}
                buyingPlanId={billing.buyingPlanId}
                changingPlanId={billing.changingPlanId}
                handleBuyPackage={billing.handleBuyPackage}
                handleChangePlan={billing.handleChangePlan}
                requestPlanChange={billing.requestPlanChange}
                cancelScheduledChange={billing.cancelScheduledChange}
                isCancellingScheduledChange={billing.isCancellingScheduledChange}
                takenOnceFreePlan={billing.takenOnceFreePlan}
              />
            )}

            {activeTab === "seats" && (
              <BillingSeatsSection packages={billing.packages} />
            )}

            {activeTab === "payment" && (
              <BillingPaymentSection
                billingAddress={billing.billingAddress}
                setBillingAddress={billing.setBillingAddress}
                secondaryEmail={billing.secondaryEmail}
                setSecondaryEmail={billing.setSecondaryEmail}
                handleSaveAddress={billing.handleSaveAddress}
                handleSaveEmail={billing.handleSaveEmail}
                isSavingAddress={billing.isSavingAddress}
                isSavingEmail={billing.isSavingEmail}
                filteredCountries={billing.filteredCountries}
                handleCountrySearch={billing.handleCountrySearch}
              />
            )}

            {activeTab === "settings" && (
              <BillingSettingsSection
                toggles={billing.toggles}
                handleToggle={billing.handleToggle}
                loadingCancelInfo={billing.loadingCancelInfo}
                handleCancelClick={billing.handleCancelClick}
                currentSubscription={billing.currentSubscription}
                handleResumeSubscription={billing.handleResumeSubscription}
                isResuming={billing.isResuming}
                handleEndTrial={billing.handleEndTrial}
                isEndingTrial={billing.isEndingTrial}
                handleManagePayment={billing.handleManagePayment}
                isManagingPayment={billing.isManagingPayment}
              />
            )}
          </>
        )}
      </Box>

      {/* Cancel subscription modal */}
      <BillingCancelModal
        open={billing.cancelModalOpen}
        onClose={() => {
          if (!billing.isCancelling) {
            billing.setCancelModalOpen(false);
            billing.setConfirmCancelText("");
          }
        }}
        isCancelling={billing.isCancelling}
        isTrialing={billing.billingPlan?.plan?.status === "trialing"}
        cancelInfo={billing.cancelInfo}
        confirmCancelText={billing.confirmCancelText}
        setConfirmCancelText={billing.setConfirmCancelText}
        handleCancelSubscription={billing.handleCancelSubscription}
      />

      {/* Change-plan confirmation modal (upgrade / downgrade / buy) */}
      <BillingChangePlanModal
        open={billing.changeModalOpen}
        onClose={() => {
          if (!billing.changingPlanId && !billing.buyingPlanId) {
            billing.setChangeModalOpen(false);
          }
        }}
        pendingChange={billing.pendingChange}
        isBusy={!!(billing.changingPlanId || billing.buyingPlanId)}
        onConfirm={billing.confirmPlanChange}
      />
    </Box>
  );
};

export default Billing;
